import { reportClientError, reportClientMessage } from '@utils/clientErrors'
import http from '@services/HTTP'
import type { CartItem } from 'store/checkout'
import type { ProductGroup } from 'types/product'

type LightProduct = { hash: string; categories?: string[]; product_group?: ProductGroup }

export const needsEnrichment = (item: CartItem) => !item.categories || item.categories.length === 0 || item.product_group === undefined

export const getVendorHash = (): string | null => {
  if (typeof window === 'undefined') return null
  const parts = window.location.pathname.split('/')
  return parts[1] === 'connect' ? parts[2] || null : null
}

export const enrichCartItemsWithCategories = async (items: CartItem[]): Promise<CartItem[]> => {
  if (!items?.length) return items

  const toEnrich = items.filter(needsEnrichment)
  if (!toEnrich.length) return items

  const vendorHash = getVendorHash()
  if (!vendorHash) return items

  // parentId is the product hash; id is the variation hash — request both so
  // items with a missing or unresolvable parentId can still match.
  const hashes = Array.from(new Set(toEnrich.flatMap(item => [item.parentId, item.id]).filter(Boolean)))

  try {
    const products = await http.post<LightProduct[], LightProduct[]>('/api/products/enrich', { hashes }, { baseURL: '', headers: { Authorization: null } })

    if (!products?.length) {
      reportClientMessage('Cart category enrichment returned no products', {
        level: 'warn',
        extra: { requestedHashes: hashes.length, itemsToEnrich: toEnrich.length }
      })
      return items
    }

    const productByHash = new Map<string, LightProduct>()
    for (const p of products) {
      productByHash.set(p.hash, p)
    }

    const unresolved: string[] = []
    const enriched = items.map(item => {
      if (!needsEnrichment(item)) return item
      const found = productByHash.get(item.parentId) ?? productByHash.get(item.id)
      if (!found) {
        unresolved.push(item.parentId || item.id)
        return item
      }
      return {
        ...item,
        // An empty server array means the product has no categories — keep whatever the item already carries.
        categories: found.categories?.length ? found.categories : item.categories,
        product_group: found.product_group ?? item.product_group
      }
    })

    if (unresolved.length) {
      reportClientMessage('Cart category enrichment left items unresolved', {
        level: 'warn',
        extra: { unresolvedHashes: unresolved, returnedProducts: products.length }
      })
    }

    return enriched
  } catch (e) {
    reportClientError(e, { tags: { source: 'enrichCartItemsWithCategories' } })
    return items
  }
}
